import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { ISiOption } from '../models/special-instructions-form.model';

/**
 * Максимум опций в выпадающем списке: mat-option рендерятся все сразу, виртуального скролла
 * в Material 14 нет. Остальное отсекается поиском.
 */
const VISIBLE_OPTIONS_LIMIT = 100;

/**
 * Пикер одного идентификатора (модификатора или размера) по справочнику из меню.
 *
 * Значение контрола — всегда сырой id; подпись собирается displayWith. Идентификатор, которого
 * нет в справочнике, не вычищается и не блокирует сохранение: он показывается как есть с
 * пометкой. Молча выкинуть — значит поменять инструкции фактом открытия экрана.
 */
@Component({
  selector: 'app-si-entity-autocomplete',
  templateUrl: './si-entity-autocomplete.component.html',
  styleUrls: ['./si-entity-autocomplete.component.scss'],
})
export class SiEntityAutocompleteComponent implements OnChanges {
  @Input() control: UntypedFormControl;
  @Input() options: ISiOption[] = [];
  @Input() labelKey = '';

  public visibleOptions: ISiOption[] = [];
  public hiddenOptionsCount = 0;

  private optionById = new Map<string, ISiOption>();

  public get isUnknown(): boolean {
    const value = this.control?.value;

    return !!value && !this.optionById.has(value);
  }

  /** Подпись выбранного id: имена модификаторов в меню не уникальны, поэтому всегда с GUID. */
  public display = (id: string): string => {
    const option = this.optionById.get(id);

    return option ? `${option.label} (${id})` : (id ?? '');
  };

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.optionById = new Map((this.options ?? []).map(option => [option.id, option]));
    }

    this.rebuildVisibleOptions(this.control?.value);
  }

  public onInput(value: string): void {
    this.rebuildVisibleOptions(value);
  }

  public trackById(_: number, option: ISiOption): string {
    return option.id;
  }

  private rebuildVisibleOptions(rawQuery: string): void {
    const query = (rawQuery ?? '').trim().toLowerCase();
    const matched = (this.options ?? []).filter(option => matches(option, query));

    this.hiddenOptionsCount = Math.max(0, matched.length - VISIBLE_OPTIONS_LIMIT);
    this.visibleOptions = matched.slice(0, VISIBLE_OPTIONS_LIMIT);
  }
}

function matches(option: ISiOption, query: string): boolean {
  if (!query) {
    return true;
  }

  return option.label.toLowerCase().includes(query) || option.hint.toLowerCase().includes(query);
}
